import {
  loadClickAnalyticsActions,
  type Result,
} from '@coveo/headless-react/ssr';
import { useEffect, useMemo, useRef } from 'react';
import { useSearchEngine } from './SearchDefinitionContext';

export const LONG_PRESS_SELECTION_DELAY_MS = 1000;

export type ResultClickHandlers = {
  onClick?: () => void;
  onContextMenu?: () => void;
  onMouseDown?: () => void;
  onMouseUp?: () => void;
  onTouchStart?: () => void;
  onTouchEnd?: () => void;
  onTouchCancel?: () => void;
};

/**
 * Result-click analytics for the SSR result card — the Headless 3 SSR package
 * exports no `buildInteractiveResult`, so this reproduces the CSR card's
 * InteractiveResult wiring with the click action loader: a mouse, keyboard, or
 * context-menu open logs one `documentOpen` per result, and a touch counts only
 * as a long press. A canceled touch ends without `touchend`, so it clears the
 * pending long press too, as does replacing the result or unmounting the card
 * (ResultList swaps result objects on visible cards when a search resolves).
 * The UA click event reaches analyticsClientMiddleware, which mirrors it to
 * the dataLayer as `search_result_click`.
 *
 * Returns no handlers before hydration (no engine to dispatch to).
 */
export function useResultClickAnalytics(result: Result): ResultClickHandlers {
  const engine = useSearchEngine();
  const longPressTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(longPressTimer.current), [engine, result]);

  return useMemo(() => {
    if (!engine) {
      return {};
    }

    let wasOpened = false;

    const select = () => {
      if (wasOpened) {
        return;
      }
      wasOpened = true;
      engine.dispatch(
        loadClickAnalyticsActions(engine).logDocumentOpen(result)
      );
    };

    const cancelLongPress = () => clearTimeout(longPressTimer.current);

    return {
      onClick: select,
      onContextMenu: select,
      onMouseDown: select,
      onMouseUp: select,
      onTouchStart: () => {
        cancelLongPress();
        longPressTimer.current = setTimeout(
          select,
          LONG_PRESS_SELECTION_DELAY_MS
        );
      },
      onTouchEnd: cancelLongPress,
      onTouchCancel: cancelLongPress,
    };
  }, [engine, result]);
}
